import { useState } from 'react';
import { PRESETS, RULE_LIMITS, dealsPerTurnOptions, rulesEqual, scheduledDealCount, type HouseRules } from '../game/match';
import { KEYS, load, save } from '../lib/storage';
import { Segmented, Stepper, Toggle } from './ui';

function usePresetNames() {
  const [names, setNames] = useState<Record<string, string>>(() => load(KEYS.presetNames, {}));
  const rename = (id: string, name: string) => {
    const next = { ...names, [id]: name };
    setNames(next);
    save(KEYS.presetNames, next);
  };
  return [names, rename] as const;
}

interface Props {
  rules: HouseRules;
  onChange: (rules: HouseRules) => void;
  /** Locked after deal 1: fields are read-only until the user unlocks. */
  locked?: boolean;
  onUnlock?: () => void;
}

export function HouseRulesPanel({ rules, onChange, locked = false, onUnlock }: Props) {
  const [names, rename] = usePresetNames();
  const [editing, setEditing] = useState<string | null>(null);
  const set = <K extends keyof HouseRules>(key: K, value: HouseRules[K]) => onChange({ ...rules, [key]: value });
  const setCourt = (patch: Partial<HouseRules['court']>) => onChange({ ...rules, court: { ...rules.court, ...patch } });

  return (
    <div className="space-y-5">
      {locked && (
        <div className="flex items-start gap-3 rounded-xl bg-amber-100 p-3 text-sm text-amber-950" role="note">
          <span aria-hidden className="text-lg">🔒</span>
          <div className="flex-1">
            <p className="font-semibold">Rules are locked because play has started.</p>
            <p>Changing them re-scores every recorded deal.</p>
            {onUnlock && (
              <button type="button" className="btn-ink btn mt-2 min-h-10 px-3 text-sm" onClick={onUnlock}>
                Unlock rules…
              </button>
            )}
          </div>
        </div>
      )}

      <fieldset disabled={locked} className="space-y-5">
        <div>
          <span className="label">Presets</span>
          <ul className="grid gap-2">
            {PRESETS.map((p) => {
              const name = names[p.id] || p.defaultName;
              // Presets describe the base rules; an extended match keeps its extra rounds.
              const presetRules = { ...p.rules, extraRounds: rules.extraRounds };
              const active = rulesEqual(presetRules, rules);
              return (
                <li key={p.id} className={`rounded-xl p-2 ring-1 ${active ? 'bg-success-700/10 ring-success-600' : 'ring-ink/15'}`}>
                  {editing === p.id ? (
                    <form
                      className="flex gap-2"
                      onSubmit={(e) => {
                        e.preventDefault();
                        const v = new FormData(e.currentTarget).get('name')?.toString().trim();
                        rename(p.id, v || p.defaultName);
                        setEditing(null);
                      }}
                    >
                      <input name="name" defaultValue={name} maxLength={30} className="field py-1.5" aria-label="Preset name" autoFocus />
                      <button className="btn-ink btn min-h-10 px-3 text-sm">Save</button>
                    </form>
                  ) : (
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        className="min-h-11 flex-1 rounded-lg px-1 text-left"
                        onClick={() => onChange(presetRules)}
                        aria-pressed={active}
                      >
                        <span className="block font-semibold">
                          {name} {active && <span className="text-success-700">✓</span>}
                        </span>
                        <span className="block text-xs text-ink-soft">{p.description}</span>
                      </button>
                      <button
                        type="button"
                        className="btn-outline-ink btn min-h-10 px-3 text-xs"
                        onClick={() => setEditing(p.id)}
                        aria-label={`Rename preset ${name}`}
                      >
                        Rename
                      </button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>

        <p className="rounded-xl bg-ink/5 p-3 text-sm text-ink-soft">
          <span className="font-semibold text-ink">Guaa scoring.</span> In every deal the team that chose Rang either
          saves itself (0) or loses and gets a Guaa (+1). Fewest Guaa wins.
        </p>

        {rules.extraRounds.length > 0 && (
          <p className="rounded-xl bg-amber-100 p-3 text-sm text-amber-950">
            Extended match: {rules.extraRounds.map((k) => `+${k}`).join(', ')} hands each ({scheduledDealCount(rules)} deals in total).
          </p>
        )}

        <Segmented
          label="Turn direction"
          value={rules.turnDirection}
          onChange={(v) => set('turnDirection', v)}
          options={[
            { value: 'anticlockwise', label: '↺ Anticlockwise' },
            { value: 'clockwise', label: '↻ Clockwise' },
          ]}
        />

        <div className="space-y-3">
          <Stepper disabled={locked}
            label="Deals per player"
            value={rules.dealsPerPlayer}
            {...RULE_LIMITS.dealsPerPlayer}
            onChange={(v) => {
              // Keep "deals per turn" a divisor of deals per player.
              const turn = Math.max(...dealsPerTurnOptions(v).filter((n) => n <= rules.dealsPerTurn));
              onChange({ ...rules, dealsPerPlayer: v, dealsPerTurn: turn });
            }}
          >
            Deals per player <span className="text-sm font-normal text-ink-soft">({scheduledDealCount(rules)} deals)</span>
          </Stepper>
          <div>
            <span className="label">Hands in a row per dealer</span>
            <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Hands in a row per dealer">
              {dealsPerTurnOptions(rules.dealsPerPlayer).map((n) => (
                <button
                  key={n}
                  type="button"
                  role="radio"
                  aria-checked={rules.dealsPerTurn === n}
                  onClick={() => set('dealsPerTurn', n)}
                  className={`min-h-11 min-w-14 rounded-xl px-3 font-semibold ring-1 ${
                    rules.dealsPerTurn === n ? 'bg-ink text-cream ring-ink' : 'bg-white ring-ink/20'
                  }`}
                >
                  {n === 1 ? 'Pass every hand' : `${n} hands`}
                </button>
              ))}
            </div>
          </div>

          <Stepper
            disabled={locked}
            label="Guaa per lost deal" value={rules.guaaPerLoss} {...RULE_LIMITS.guaaPerLoss} onChange={(v) => set('guaaPerLoss', v)} />
        </div>

        <div className="space-y-2 rounded-xl bg-ink/5 p-3">
          <Toggle
            label="Court / Kot"
            hint="Extra Guaa when the Rang team is swept."
            checked={rules.court.enabled}
            onChange={(enabled) => setCourt({ enabled })}
          />
          {rules.court.enabled && (
            <>
              <Stepper disabled={locked} label="Court: extra Guaa" value={rules.court.bonus} {...RULE_LIMITS.courtBonus} onChange={(bonus) => setCourt({ bonus })} />
              <Toggle
                label="Court = first 7 tricks in a row"
                hint="Off: the Rang team must lose all 13 tricks."
                checked={rules.court.mode === 'first7InRow'}
                onChange={(on) => setCourt({ mode: on ? 'first7InRow' : 'all13' })}
              />
            </>
          )}
        </div>

        <div>
          <label className="label" htmlFor="trump-caller">
            Who chooses Rang (trump)
          </label>
          <select
            id="trump-caller"
            className="field"
            value={rules.trumpCaller}
            onChange={(e) => set('trumpCaller', e.target.value as HouseRules['trumpCaller'])}
          >
            <option value="afterDealer">Player after the dealer</option>
            <option value="dealer">The dealer</option>
            <option value="highestBidder">Highest bidder (decided at the table)</option>
          </select>
        </div>

        <Toggle
          label="End early when decided"
          hint="Stop once the trailing team can't catch up."
          checked={rules.endEarlyWhenDecided}
          onChange={(v) => set('endEarlyWhenDecided', v)}
        />


      </fieldset>
    </div>
  );
}
